import { IdGenerator, IdGeneratorEntityType } from '../services/IdGenerator';
import { trimTextAndFilterOutWhitespaces } from '../services/punctuation';
import { ClipTranscriptSegment } from './ClipTranscriptSegment';
import { RemovedTranscriptClip } from './elements/RemovedTranscriptClip';
import { VideoTranscriptElement } from './elements/VideoTranscriptElement';
import { Track } from './Track';

import type { HasClipTranscriptSegment } from './elements/HasClipTranscriptSegment';
import type { VideoElement } from './elements/VideoElement';
import type { TranscriptElementLegacy } from '@src/types.ts/transcript';

/**
 * TranscriptTrack extends Track to handle elements that implement HasClipTranscriptSegment
 * Internally stores HasClipTranscriptSegment elements but provides typed access to VideoTranscriptElement
 */
export class TranscriptTrack extends Track {
  private _transcriptSegmentElements: HasClipTranscriptSegment[];

  constructor(props: Partial<TranscriptTrack & Track> = {}) {
    super(props);
    this._transcriptSegmentElements = props.transcriptSegmentElements
      ? [...props.transcriptSegmentElements]
      : [];
  }

  /**
   * Static factory method to create a TranscriptTrack from a video track and TranscriptElements
   * Creates VideoTranscriptElements for video elements and RemovedTranscriptClips for gaps between them
   */
  static fromVideoTrackAndTranscript(
    videoTrack: Track,
    transcriptElements: readonly TranscriptElementLegacy[],
    originalTranscriptElements: readonly TranscriptElementLegacy[],
    skipIdGeneratorSeeding?: boolean,
  ): TranscriptTrack {
    const activeTranscriptElements = trimTextAndFilterOutWhitespaces(
      transcriptElements.filter(
        el => el.state !== 'removed' && el.state !== 'cut',
      ),
    );

    const originalTranscriptElementsFiltered = trimTextAndFilterOutWhitespaces(
      originalTranscriptElements,
    );
    // Setup ID generator for TranscriptToken if not already set up

    // Only care to keep IDs of active transcript elements intact between sessions since other parts like karaoke relies on it.
    // removed/cut elements will be regenerated, their IDs can change between sessions for now. I want to avoid IDs growing because of removed elements regenerating from current max id every session. This is why IdGenerator keeps available ranges to reuse Ids of removed elements.
    if (!skipIdGeneratorSeeding) {
      const tokenGenerator = IdGenerator.get(
        IdGeneratorEntityType.TranscriptToken,
        'hex',
      );
      tokenGenerator.setupFromCollection(activeTranscriptElements);
    }
    // Get video elements from the track, ensuring they are VideoElements
    const videoElements = videoTrack.elements.filter(
      (el): el is VideoElement => el.type === 'video',
    );

    // Sort video elements by start time
    const sortedVideoElements = videoElements.sort((a, b) => a.time - b.time);

    const transcriptSegmentElements: HasClipTranscriptSegment[] = [];
    const ascendingSequenceVideoElementsIndexes =
      TranscriptTrack.findLongestAscendingSequence(videoElements);
    let prevSequenceElement: VideoElement | null = null;
    for (let i = 0; i < sortedVideoElements.length; i++) {
      const videoElement = sortedVideoElements[i];

      if (ascendingSequenceVideoElementsIndexes.includes(i)) {
        // If there's a gap between video elements create RemovedTranscriptClip
        if (
          videoElement.getMediaStartTime() >
          (prevSequenceElement?.getMediaEndTime() ?? 0)
        ) {
          // For gaps, use original transcript with media times
          const gapClipTranscriptSegment =
            ClipTranscriptSegment.createFromElements(
              originalTranscriptElementsFiltered,
              prevSequenceElement?.getMediaEndTime() ?? 0,
              videoElement.getMediaStartTime(),
              prevSequenceElement?.getMediaEndTime() ?? 0,
              videoElement.getMediaStartTime(),
            );

          const removedTranscriptClip = new RemovedTranscriptClip({
            clipTranscriptSegment: gapClipTranscriptSegment,
          });

          transcriptSegmentElements.push(removedTranscriptClip);
        }
        prevSequenceElement = videoElement;
      }

      if (ascendingSequenceVideoElementsIndexes.includes(i)) {
        // If there's a gap between video elements create RemovedTranscriptClip
        if (
          videoElement.getMediaStartTime() >
          (prevSequenceElement?.getMediaEndTime() ?? 0)
        ) {
          // For gaps, use original transcript with media times
          const gapClipTranscriptSegment =
            ClipTranscriptSegment.createFromElements(
              originalTranscriptElements,
              prevSequenceElement?.getMediaEndTime() ?? 0,
              videoElement.getMediaStartTime(),
              prevSequenceElement?.getMediaEndTime() ?? 0,
              videoElement.getMediaStartTime(),
            );

          const removedTranscriptClip = new RemovedTranscriptClip({
            clipTranscriptSegment: gapClipTranscriptSegment,
          });

          transcriptSegmentElements.push(removedTranscriptClip);
        }
        prevSequenceElement = videoElement;
      }

      // Create VideoTranscriptElement for this video element
      // Use current transcript with timeline start/end times and media start/end times
      const clipTranscriptSegment = ClipTranscriptSegment.createFromElements(
        activeTranscriptElements,
        videoElement.time,
        videoElement.end_time,
        videoElement.getMediaStartTime(),
        videoElement.getMediaEndTime(),
      );

      if (!ascendingSequenceVideoElementsIndexes.includes(i)) {
        clipTranscriptSegment.markElementsAsAdded();
      } else {
        clipTranscriptSegment.unmarkElements();
      }

      const videoTranscriptElement = new VideoTranscriptElement({
        ...videoElement.getState(),
        clipTranscriptSegment: clipTranscriptSegment,
      });

      transcriptSegmentElements.push(videoTranscriptElement);
    }
    if (
      prevSequenceElement &&
      transcriptSegmentElements.at(-1) instanceof VideoTranscriptElement
    ) {
      // add RemovedTranscriptClip if element media end time is less then last original transcript element time

      if (prevSequenceElement.media_duration == null) {
        throw new Error(
          'VideoTranscriptElement must have media_duration set to create a TranscriptTrack',
        );
      }

      const tailClipTranscriptSegment =
        ClipTranscriptSegment.createFromElements(
          originalTranscriptElementsFiltered,
          prevSequenceElement.getMediaEndTime(),
          prevSequenceElement.media_duration,
          prevSequenceElement.getMediaEndTime(),
          prevSequenceElement.media_duration,
        );

      const removedTranscriptClip = new RemovedTranscriptClip({
        clipTranscriptSegment: tailClipTranscriptSegment,
      });

      transcriptSegmentElements.push(removedTranscriptClip);
    }

    // Create the TranscriptTrack with the generated elements
    return new TranscriptTrack({
      id: videoTrack.id,
      type: videoTrack.type,
      name: videoTrack.name,
      locked: videoTrack.locked,
      visible: videoTrack.visible,
      transcriptSegmentElements,
    });
  }

  /**
   * Get elements as VideoTranscriptElement array for external access
   * This ensures type safety for code that expects only VideoTranscriptElement
   */
  override get elements(): readonly VideoTranscriptElement[] {
    return this.transcriptSegmentElements.filter(
      (el): el is VideoTranscriptElement =>
        el instanceof VideoTranscriptElement,
    );
  }

  /**
   * Get all HasClipTranscriptSegment elements (including VideoTranscriptElement and others)
   * This allows internal code to work with the broader HasClipTranscriptSegment interface
   */
  get transcriptSegmentElements(): readonly HasClipTranscriptSegment[] {
    return this._transcriptSegmentElements;
  }

  /**
   * Find VideoTranscriptElement at specific time
   */
  override findElementAtTime(time: number): VideoTranscriptElement | null {
    for (const el of this.elements) {
      if (el.time <= time && time < el.end_time) {
        return el;
      }
    }
    return null;
  }

  /**
   * Find HasClipTranscriptSegment element at specific time
   */
  findTranscriptElementAtTime(time: number): HasClipTranscriptSegment | null {
    for (const el of this._transcriptSegmentElements) {
      if (
        'time' in el &&
        'end_time' in el &&
        (el as any).time <= time &&
        time < (el as any).end_time
      ) {
        return el;
      }
    }
    return null;
  }

  override printElements(): void {
    // Sort and print VideoTranscriptElements
    const sortedElements = this.sortedElements;
    console.log(
      `TranscriptTrack ${this.name} (${this.type}) - VideoTranscriptElements:`,
      sortedElements.map(el => ({
        id: el.id,
        type: el.type,
        time: el.time,
        end_time: el.end_time,
        track: el.track,
      })),
    );
  }

  /**
   * @returns {VideoTranscriptElement[]} An array of VideoTranscriptElements open for editing
   */
  override get mutableElements(): VideoTranscriptElement[] {
    return this.elements.filter(el => !el.locked);
  }

  /**
   * @returns {HasClipTranscriptSegment[]} An array of HasClipTranscriptSegment elements open for editing
   */
  get mutableTranscriptElements(): HasClipTranscriptSegment[] {
    return this._transcriptSegmentElements.filter(el =>
      'locked' in el ? !(el as any).locked : true,
    );
  }

  /**
   * Adds a VideoTranscriptElement to the track
   */
  override addElement(element: VideoTranscriptElement): void {
    super.addElement(element);
    // Also add to transcript elements if it implements HasClipTranscriptSegment
    if (this.implementsHasClipTranscriptSegment(element)) {
      this._transcriptSegmentElements = [
        ...this._transcriptSegmentElements,
        element,
      ];
    }
  }

  /**
   * Adds a HasClipTranscriptSegment element to the track at position in array
   */
  addTranscriptSegmentElement(
    element: HasClipTranscriptSegment,
    position: number,
  ): void {
    this._transcriptSegmentElements = [
      ...this._transcriptSegmentElements.slice(0, position),
      element,
      ...this._transcriptSegmentElements.slice(position),
    ];
  }

  removeTranscriptSegmentElements(start: number, deleteCount: number): boolean {
    // remove range and return removed elements
    const removed = this._transcriptSegmentElements.splice(start, deleteCount);
    return removed.length > 0;
  }

  /**
   * Removes an element from the track by id
   */
  override removeElement(elementId: string): boolean {
    const segmentElementIndex = this._transcriptSegmentElements.findIndex(el =>
      'id' in el ? el.id === elementId : false,
    );

    if (segmentElementIndex === -1) {
      return false; // Element not found
    }

    // replace with a removed clip segment
    const removedElement = this._transcriptSegmentElements[segmentElementIndex];
    this._transcriptSegmentElements.splice(
      segmentElementIndex,
      1,
      new RemovedTranscriptClip({
        clipTranscriptSegment: removedElement.getClipTranscriptSegment(),
      }),
    );

    const removedClipResult = new RemovedTranscriptClip({
      clipTranscriptSegment: removedElement.getClipTranscriptSegment(),
    });

    // merge with previous or next removed clip if applicable
    this.mergeWithSurroundingRemovedClips(segmentElementIndex);

    return removedClipResult !== null;
  }

  /**
   * Merge a removed clip with surrounding removed clips if they are contiguous
   */
  mergeWithSurroundingRemovedClips(elementPosition: number): void {
    const removedClip = this._transcriptSegmentElements[elementPosition];
    if (!(removedClip instanceof RemovedTranscriptClip)) {
      console.warn('Element at position is not a RemovedTranscriptClip');
      return;
    }

    const segment = removedClip.getClipTranscriptSegment();
    const prevElement = this._transcriptSegmentElements[elementPosition - 1];
    const nextElement = this._transcriptSegmentElements[elementPosition + 1];

    let mergeWithPrev = false;
    let mergeWithNext = false;

    // Check if we can merge with previous removed clip
    if (prevElement instanceof RemovedTranscriptClip) {
      const prevSegment = prevElement.getClipTranscriptSegment();
      if (Math.abs(prevSegment.mediaEnd - segment.mediaStart) < 0.001) {
        mergeWithPrev = true;
      }
    }

    // Check if we can merge with next removed clip
    if (nextElement instanceof RemovedTranscriptClip) {
      const nextSegment = nextElement.getClipTranscriptSegment();
      if (Math.abs(segment.mediaEnd - nextSegment.mediaStart) < 0.001) {
        mergeWithNext = true;
      }
    }

    // Handle merging with adjacent removed clips using concat
    if (mergeWithPrev && mergeWithNext) {
      // Merge all three: prev + current + next using concat
      const prevClip = prevElement as RemovedTranscriptClip;
      const nextClip = nextElement as RemovedTranscriptClip;

      // Use concat to merge prev + current + next
      const tempMerged = prevClip.getClipTranscriptSegment().concat(segment);
      const finalMerged = tempMerged.concat(
        nextClip.getClipTranscriptSegment(),
      );

      const mergedClip = new RemovedTranscriptClip({
        clipTranscriptSegment: finalMerged,
      });

      // Remove all three clips (prev, current, next) and insert merged clip
      this.removeTranscriptSegmentElements(elementPosition - 1, 3);
      this.addTranscriptSegmentElement(mergedClip, elementPosition - 1);
    } else if (mergeWithPrev) {
      // Merge with previous clip using concat
      const prevClip = prevElement as RemovedTranscriptClip;
      const mergedSegment = prevClip.getClipTranscriptSegment().concat(segment);

      const mergedClip = new RemovedTranscriptClip({
        clipTranscriptSegment: mergedSegment,
      });

      // Remove both clips (prev and current) and add merged clip
      this.removeTranscriptSegmentElements(elementPosition - 1, 2);
      this.addTranscriptSegmentElement(mergedClip, elementPosition - 1);
    } else if (mergeWithNext) {
      // Merge with next clip using concat
      const nextClip = nextElement as RemovedTranscriptClip;
      const mergedSegment = segment.concat(nextClip.getClipTranscriptSegment());

      const mergedClip = new RemovedTranscriptClip({
        clipTranscriptSegment: mergedSegment,
      });

      // Remove both clips (current and next) and add merged clip at current position
      this.removeTranscriptSegmentElements(elementPosition, 2);
      this.addTranscriptSegmentElement(mergedClip, elementPosition);
    }
    // If no merging is possible, the removed clip is already in place from removeElementWithReplacedClip
  }

  /**
   * Updates a VideoTranscriptElement in the track
   */
  override updateElement(
    elementId: string,
    props: Partial<VideoTranscriptElement>,
  ): boolean {
    return super.updateElement(elementId, props);
  }

  /**
   * Updates a HasClipTranscriptSegment element in the track
   */
  updateTranscriptElement(
    elementId: string,
    props: Partial<HasClipTranscriptSegment>,
  ): boolean {
    for (const element of this._transcriptSegmentElements) {
      if ('id' in element && element.id === elementId) {
        if ('update' in element && typeof element.update === 'function') {
          element.update(props);
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Replaces a VideoTranscriptElement with a new element while preserving its position in the track
   */
  override replaceElement(
    elementId: string,
    newElement: VideoTranscriptElement,
  ): boolean {
    const replaced = super.replaceElement(elementId, newElement);

    // Also replace in transcript elements if applicable
    if (replaced && this.implementsHasClipTranscriptSegment(newElement)) {
      const transcriptIndex = this._transcriptSegmentElements.findIndex(
        el => 'id' in el && (el as any).id === elementId,
      );
      if (transcriptIndex !== -1) {
        this._transcriptSegmentElements[transcriptIndex] = newElement;
      }
    }

    return replaced;
  }

  /**
   * Returns all VideoTranscriptElements sorted by start time
   */
  override get sortedElements(): readonly VideoTranscriptElement[] {
    return [...this.elements].sort((a, b) => a.time - b.time);
  }

  /**
   * Checks if an element can be added at the specified time without overlapping
   */
  override canAddElementAt(startTime: number, duration: number): boolean {
    return super.canAddElementAt(startTime, duration);
  }

  /**
   * Creates a deep clone of this track
   */
  override clone(): TranscriptTrack {
    return new TranscriptTrack({
      id: this.id,
      type: this.type,
      name: this.name,
      elements: this.elements.map(element => element.clone()),
      locked: this.locked,
      visible: this.visible,
      transcriptSegmentElements: this._transcriptSegmentElements.map(element =>
        'clone' in element && typeof (element as any).clone === 'function'
          ? (element as any).clone()
          : element,
      ),
    });
  }

  /**
   * Converts the track to JSON compatible with the Renderer
   */
  override toJSON(): Record<string, any>[] {
    return this.elements.map(element => element.toJSON());
  }

  /**
   * Helper method to check if an object implements HasClipTranscriptSegment
   */
  private implementsHasClipTranscriptSegment(
    obj: any,
  ): obj is HasClipTranscriptSegment {
    return (
      obj &&
      typeof obj.getMediaStartTime === 'function' &&
      typeof obj.getMediaEndTime === 'function' &&
      typeof obj.getClipTranscriptSegment === 'function' &&
      typeof obj.getActiveTranscriptText === 'function'
    );
  }

  static findLongestAscendingSequence(videoElements: VideoElement[]): number[] {
    if (videoElements.length === 0) return [];

    // Create array of indices with their corresponding media start times and durations
    const indexed = videoElements.map((element, index) => ({
      index,
      mediaStart: element.getMediaStartTime(),
      duration: element.getMediaEndTime() - element.getMediaStartTime(),
    }));

    // Sort by media start time for processing
    // indexed.sort((a, b) => a.mediaStart - b.mediaStart);

    const n = indexed.length;
    const dp = new Array(n).fill(0); // dp[i] = max duration ending at position i
    const prev = new Array(n).fill(-1); // for reconstructing the sequence

    // Initialize with individual durations
    for (let i = 0; i < n; i++) {
      dp[i] = indexed[i].duration;
    }

    // Fill dp array using dynamic programming
    for (let i = 1; i < n; i++) {
      for (let j = 0; j < i; j++) {
        // Check if elements can be in ascending sequence (non-overlapping)
        if (indexed[j].mediaStart <= indexed[i].mediaStart) {
          const newDuration = dp[j] + indexed[i].duration;
          if (newDuration > dp[i]) {
            dp[i] = newDuration;
            prev[i] = j;
          }
        }
      }
    }

    // Find the position with maximum duration
    let maxDuration = 0;
    let maxIndex = 0;
    for (let i = 0; i < n; i++) {
      if (dp[i] > maxDuration) {
        maxDuration = dp[i];
        maxIndex = i;
      }
    }

    // Reconstruct the sequence indices
    const sequence: number[] = [];
    let current = maxIndex;
    while (current !== -1) {
      sequence.unshift(indexed[current].index);
      current = prev[current];
    }

    return sequence;
  }
}
